// 比較表ページ本体（sections/aicon-compare-table.liquid の器を埋める）。
//
// 真実の源は URL の ?products=handle,handle。無ければ localStorage（一覧のチェックが保存する
// 選択状態）から復元して URL に正規化する。これで比較URLをそのまま共有できる。
// ?ai=handle が付いていれば、その handle の列に「AIのおすすめ」を出す（診断SPA からの遷移／T-22）。
//
// 列データ（総額・仕様・機能）は Section Rendering API（aicon-compare-column）から、
// 試算3行（電気代/年・10年総コスト・グレード）だけ backend 比較APIから取る。
// 比較APIが落ちても表は成立し、試算セルが「−」になり警告バナーが出る（fail-soft）。
//
// ★ 行の構成（T-23 で整理）★
//   機種 / 本体価格 / 電気代 per 年 / 10年総コスト / グレード / 注記 / CTA
//
// ★ 仕様行・機能行と「仕様と機能をすべて見る」の折りたたみは廃止（8/19 日高さん指示）★
// 機種を足すほど際限なく行が伸び、モバイルで表が長くなりすぎるため。仕様の詳細は
// 各商品ページ（「この機種を見る」の先）にある。Section Rendering の payload
// （sections/aicon-compare-column.liquid の specs / features）は他で使う余地を残して
// そのままにしてあるが、この表では読んでいない。

const STORAGE_KEY = 'aicon:compare:v1';
// ★ AI の提案は URL だけで受け渡す（日高さん指示 2026-08-21）★
// 「キャッシュ系はゾンビバグの温床になるので一切無いように」との指示のとおり、
// sessionStorage 等には**一切保存しない**。?ai=<handle,...>&aiq=<quoteId> が
// 唯一の出どころで、URL に無ければ AI 列は出ない。
//
// 保存を持たない代わりに、一覧へ機種を足しに行って比較バーで戻ってくると
// （遷移先は assets/aicon-compare.js の compareUrl ＝ /pages/compare 固定で
// ?ai= を引き継がない）AI 列は消える。診断からもう一度来れば復活する。
// 「消える」は説明できる挙動で、「前の診断の提案が残っている」は説明できない。
const AI_QUERY_KEY = 'ai';
const AI_QUOTE_QUERY_KEY = 'aiq';
/** AI 提案の上限。診断SPA のスロットは4つだが、将来増えても表が壊れないよう5で止める */
const AI_MAX_PICKS = 5;
// AI の選定理由の折りたたみ（2026-08-25）。畳んだときの行数は CSS の
// .aicon-compare-page__aiWhy の -webkit-line-clamp と対で決まる。
//
// ★ 畳む側だけ ▲ を付ける（日高さん指示 2026-08-25）★
//   工事可能日バーの閉じる導線（sections/aicon-calendar-bar.liquid:115-120）と
//   同じ作りにする。三角の向きで「上にたたむ」ことを示し、三角は aria-hidden に
//   して読み上げからは外す（読み上げは「畳む」だけになる）。
const WHY_MORE_LABEL = '続きを読む';
const WHY_LESS_LABEL = '畳む';
const WHY_LESS_CARET = '\u25B2'; // ▲
const CHANGE_EVENT = 'aicon:compare:change';
const COLUMN_SECTION = 'aicon-compare-column';

// ★ 1位の向き ★
// 金額系（本体価格・電気代・10年総コスト）は小さいほど良い。
// 取り違えると「いちばん高い機種」を1位として見せることになるので、行ごとに明示する。
const LOWER_IS_BETTER = 'min';
const HIGHER_IS_BETTER = 'max';

const yen = (value) => `${new Intl.NumberFormat('ja-JP').format(value)}円`;

// aicon-compare.js の readSelection と同じ正規化を必ず通す（不正エントリが
// 1件混じっただけで比較表ページ全体が初期化に失敗するのを防ぐ）
const readStorage = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => item && item.handle) : [];
  } catch {
    return [];
  }
};

const writeStorage = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // プライベートモード等で保存できなくても比較表自体は動かす
  }
  // 同一タブに選択バー等が同居した場合にも変更が伝わるようにする
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

// aicon.model_no は手編集され得る metafield で前後空白が混じることがある。
// 比較APIはトリム済みの queried を返すため、送信・突合の両方でこのキーを使う。
const modelKey = (column) => (column.model_no || '').trim();

/** 読み上げ専用テキスト。○/✕ だけでは意味を持たないので必ず添える */
class AiconCompareTable extends HTMLElement {
  connectedCallback() {
    // 上限は sections/aicon-compare-table.liquid の data-max（既定3）。
    // 一覧側 assets/aicon-compare.js の MAX_MODELS と必ず揃えること。
    this.maxModels = Number(this.dataset.max) || 3;
    this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');
    // 「比較から外す」→ 再 render 中に、前回の比較API応答が新しい表へ
    // 書き込まれるのを防ぐための世代カウンタ
    this.renderGeneration = 0;
    this.refs = {};
    for (const el of this.querySelectorAll('[data-ref]')) {
      this.refs[el.dataset.ref] = el;
    }
    this.handles = this.resolveHandles();
    this.render();
  }

  resolveHandles() {
    const params = new URLSearchParams(location.search);
    const fromUrl = (params.get('products') || '')
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);

    // ★ 診断SPA から AI の提案が渡ってくる（T-22 / 2026-08-21 で複数化）★
    //   ?ai=handle,handle,...  提案された機種（最大5件・順位順）
    //   ?aiq=<quoteId>         その提案がどの診断のものか
    //
    // ★ quoteId でゾンビを断つ ★
    // 保存は quoteId ごと。別の診断をやり直せば quoteId が変わるので、前の提案は
    // 構造的に読めなくなる。?aiq= が無い訪問（一覧の比較バー経由など）では
    // 読まずに残骸を消す。「残っているのに使う」経路を作らない。
    const aiQuoteId = (params.get(AI_QUOTE_QUERY_KEY) || '').trim();
    const aiParam = (params.get(AI_QUERY_KEY) || '')
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean)
      .slice(0, AI_MAX_PICKS);

    // 文言（バッジ・理由・アイロの一言）は fillEstimates が backend から取る。
    // URL には handle しか載せない＝古い文言が出る余地が無い。
    const aiPicks =
      aiQuoteId && aiParam.length > 0 ? aiParam.map((handle) => ({ handle, badge: '', catchCopy: '', reason: '' })) : [];
    const aiHandles = aiPicks.map((p) => p.handle);

    // URL で機種を渡されて来たか（診断からの遷移・共有リンク）。
    // URL 由来のときだけ選択に取り込む（下の adoptUrlSelection）。
    this.fromUrl = fromUrl.length > 0;
    const picked = fromUrl.length > 0 ? fromUrl : readStorage().map((item) => item.handle).filter(Boolean);

    // ★ AI の提案は maxModels の外側に置く ★
    // ユーザーが自分で選べるのは今までどおり maxModels 件（既定3）。診断を通っていれば
    // そこに AI の提案（最大5件）が加わる。診断を通っていなければ aiPicks は空なので
    // 従来どおり3列までで、挙動は変わらない。
    const users = [...new Set(picked)].filter((h) => !aiHandles.includes(h)).slice(0, this.maxModels);
    this.aiQuoteId = aiQuoteId || null;
    this.aiPicks = aiPicks;
    // 文言は fillEstimates が backend から取って applyRecommendation で入れる
    this.aiSummary = '';
    // ★ aiHandle は持たない（2026-08-21）★
    //   複数対応にした際、単一 handle を見ている箇所が3つ残っていて
    //   「2列目以降が枠の外／お客様の枠を食う／印が付かない」を起こしていた。
    //   参照を全て aiPicks に寄せたので、単一の handle は保持しない。

    // AI列は先頭（順位順）。
    return [...aiHandles, ...users];
  }

  /**
   * 「＋ 機種を追加する」の遷移先に AI の提案を引き継ぐ（日高さん指摘 2026-08-21）。
   *
   * ★ 保存を持たないので URL で運ぶしかない ★
   * AI の提案は ?ai=&?aiq= だけが出どころ（保存はゾンビの温床になるため持たない）。
   * 一覧へ機種を足しに行くとこの2つが落ちて AI 列が消えていた。
   * 一覧へのリンクに載せ、一覧の比較バー（assets/aicon-compare.js）が
   * 比較ページのURLへ載せ直すことで、保存なしで往復できる。
   */
  syncAddLink() {
    const link = this.refs?.add;
    if (!link) return;
    let url;
    try {
      url = new URL(link.getAttribute('href') || '/', location.origin);
    } catch {
      return;
    }
    const picks = (this.aiPicks || []).map((p) => p.handle).filter(Boolean);
    if (picks.length > 0 && this.aiQuoteId) {
      url.searchParams.set(AI_QUERY_KEY, picks.join(','));
      url.searchParams.set(AI_QUOTE_QUERY_KEY, this.aiQuoteId);
    } else {
      url.searchParams.delete(AI_QUERY_KEY);
      url.searchParams.delete(AI_QUOTE_QUERY_KEY);
    }
    link.setAttribute('href', `${url.pathname}${url.search}`);
  }

  /**
   * 「この機種を見る」の先に、診断の見積ID（?aiq=）を付ける（2026-10-17）。
   *
   * ★ 付けないと注文と診断のつながりが切れる ★
   *   商品ページで写真見積をやり直すと新しい見積ができ、注文はそちらに紐づく。
   *   ID を運ばないと「診断を通って買った注文」が記録上は診断を通っていない注文に
   *   見える（#1158・#1159 の調査）。商品ページ（assets/aicon-install-quote.js）が
   *   この ID を見積に残す。
   *   診断を通っていない（aiQuoteId が無い）ときは今までどおりの URL。
   */
  productHrefWithDiagnosis(href) {
    if (!this.aiQuoteId || !href) return href;
    try {
      const url = new URL(href, location.origin);
      url.searchParams.set(AI_QUOTE_QUERY_KEY, this.aiQuoteId);
      // # 以降も残す（今の product.url には無いが、付いたときに落とさない・PR-Agent /improve）
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return href;
    }
  }

  syncUrl() {
    const url = new URL(location.href);
    if (this.handles.length > 0) {
      url.searchParams.set('products', this.handles.join(','));
    } else {
      url.searchParams.delete('products');
    }
    // AI提案の機種を比較から外した / 取得できなかった場合は ai= も落とす。
    // 存在しない列を指したままの URL を共有させない。URL が唯一の出どころなので、
    // ここから消えれば復活する経路も無い。
    const remaining = (this.aiPicks || []).filter((p) => this.handles.includes(p.handle));
    this.aiPicks = remaining;
    if (remaining.length > 0 && this.aiQuoteId) {
      url.searchParams.set(AI_QUERY_KEY, remaining.map((p) => p.handle).join(','));
      url.searchParams.set(AI_QUOTE_QUERY_KEY, this.aiQuoteId);
    } else {
      url.searchParams.delete(AI_QUERY_KEY);
      url.searchParams.delete(AI_QUOTE_QUERY_KEY);
    }
    history.replaceState(null, '', url);
    this.syncAddLink();
  }

  async render() {
    const generation = ++this.renderGeneration;
    this.syncUrl();
    if (this.handles.length === 0) {
      this.showEmpty();
      return;
    }

    this.refs.empty.hidden = true;
    this.refs.scroll.hidden = false;
    this.refs.heading.textContent = `${this.handles.length}機種を比較する`;

    // ★ アイロの一言（2026-08-21）★
    //   診断SPA が生成した文（LLM）を出す。診断を通っていない訪問では
    //   this.aiSummary が空なので吹き出しごと出さない。
    if (this.refs.airo && this.refs.airoText) {
      const summary = (this.aiSummary || '').trim();
      this.refs.airoText.textContent = summary;
      this.refs.airo.hidden = summary === '';
    }

    const columns = (
      await Promise.all(this.handles.map((handle) => this.fetchColumn(handle)))
    ).filter(Boolean);
    if (generation !== this.renderGeneration) return; // 途中で再 render された

    // 取扱終了などで列が取得できなかった handle は選択からも取り除く
    if (columns.length !== this.handles.length) {
      // ★ 何が落ちたか残す（日高さん指摘 2026-08-21）★
      //   列が黙って消えるため「AIが3件提案したのに1列しか出ない」の原因が
      //   追えなかった。落ちる理由は /products/<handle> が 404（未公開・取扱終了）か、
      //   セクションの JSON が読めないかのどちらか。切り分けできるようにする。
      const got = new Set(columns.map((c) => c.handle));
      const dropped = this.handles.filter((h) => !got.has(h));
      console.warn(
        '[aicon-compare] 列を取得できなかった機種を比較から外しました:',
        dropped.join(', '),
        '（/products/<handle> が 404 か、商品がオンラインストアに未公開の可能性）',
      );
      this.handles = columns.map((column) => column.handle);
      writeStorage(readStorage().filter((item) => this.handles.includes(item.handle)));
      this.syncUrl();
      if (columns.length === 0) {
        this.showEmpty();
        return;
      }
      this.refs.heading.textContent = `${this.handles.length}機種を比較する`;
      // ★ 画面にも出す（日高さん指摘 2026-08-21）★
      //   console.warn だけでは、AI が3件提案したのに1列しか出ない状態が
      //   「そういう仕様」に見えてしまう。何件落ちたかを画面に残す。
      this.showDroppedNotice(dropped.length);
    }

    this.adoptUrlSelection(columns);
    this.syncAddVisibility(columns);
    this.buildTable(columns);
    this.positionAiFrame();
    this.positionAiTags();
    this.observeTableResize();
    this.refs.live.textContent = `${columns.length}機種の比較表を表示しました`;
    await this.fillEstimates(columns, generation);
  }

  /**
   * URL で渡された機種を「お客様の選択」（aicon:compare:v1）に取り込む。
   *
   * ★ なぜ要るか ★
   * 比較ページの「＋ 機種を追加する」は一覧へ飛び、戻りは比較バーの
   * /pages/compare 固定（assets/aicon-compare.js の compareUrl）でパラメータを持たない。
   * URL にしか無い機種は保存されないため、**一覧へ1件足しに行って戻るだけで
   * 入口で見ていた機種の列が消える**。診断は商品ページからも始められる
   * （lib/diagnosis-entry.ts の ?model=）ので、これは通常経路で起きる。
   * 取り込んでおけば、以降どの経路で来ても保存だけを見れば列が揃う。
   *
   * ★ AI の handle は入れない ★
   * AI の提案は URL（?ai=）だけで持っており、ここに混ぜるとお客様の3枠のうち
   * 1つを食う＝合計4列にならない。二重管理にもなる。
   *
   * ★ 一覧のチェックが入るのは正しい ★
   * assets/aicon-compare.js の isSelected() は「その handle が比較に入っているか」
   * だけを見ている。チェック＝「一覧でクリックした」ではなく「比較に入っている」
   * なので、表に出ている以上 ON が実態と合う。
   *
   * ★ 中身が同じなら書かない ★
   * writeStorage は CHANGE_EVENT を飛ばす。再 render のたびに書くと、同居する
   * 選択バーが毎回描き直される。
   */
  adoptUrlSelection(columns) {
    if (!this.fromUrl) return;
    // ★ AI提案は「お客様の選択」に取り込まない（複数対応・2026-08-21）★
    //   1台だけ除いていたため、2列目以降の AI提案がお客様の3枠を食っていた。
    const aiHandles = new Set((this.aiPicks || []).map((p) => p.handle));
    const next = columns
      .filter((column) => !aiHandles.has(column.handle))
      .slice(0, this.maxModels)
      .map((column) => ({
        handle: column.handle,
        title: column.title || '',
        image: column.image || '',
        model_no: column.model_no || '',
      }));
    const current = readStorage();
    const same =
      current.length === next.length &&
      current.every((item, i) => item.handle === next[i].handle);
    if (same) return;
    writeStorage(next);
  }

  /**
   * AI列の外枠（sections/aicon-compare-table.liquid の .aicon-compare-page__ai-frame）を
   * 実際の列の位置に合わせる。
   *
   * 枠を td の border で描くと「仕様と機能をすべて見る」の行（colspan で全列を横断）に
   * AI列の td が無く、そこで囲みが途切れる。表に1枚重ねる方式にして、行の構成に
   * 依存しない連続した枠にしている。
   *
   * 呼ぶタイミング: 表を組んだ直後 / 折りたたみの開閉後 / 表のサイズが変わったとき。
   * 高さは表そのものから取るので、開閉で行が増減しても追従する。
   */
  positionAiFrame() {
    const frame = this.refs.aiFrame;
    const stage = this.refs.stage;
    if (!frame || !stage) return;

    // ★ AI列は複数ある（2026-08-21）★
    //   1列ぶんしか囲んでいなかったため、2列目以降が枠の外に出ていた。
    //   AI列は常に先頭から連続して並ぶ（handles の組み立て順）ので、
    //   最初と最後のセルから左端と右端を取れば全体を囲める。
    const cells = this.refs.table.querySelectorAll('td.is-ai');
    if (cells.length === 0) {
      frame.hidden = true;
      return;
    }

    const stageRect = stage.getBoundingClientRect();
    const tableRect = this.refs.table.getBoundingClientRect();
    // 同じ行のセルだけを見る（列ごとに1つ拾えば足りる）。
    // querySelectorAll は全行ぶん返すので、左端・右端を実測で取る
    let left = Infinity;
    let right = -Infinity;
    for (const cell of cells) {
      const r = cell.getBoundingClientRect();
      if (r.width === 0) continue; // 非表示行（折りたたみ内）は無視する
      left = Math.min(left, r.left);
      right = Math.max(right, r.right);
    }
    if (!Number.isFinite(left) || !Number.isFinite(right)) {
      frame.hidden = true;
      return;
    }
    frame.style.left = `${left - stageRect.left}px`;
    frame.style.width = `${right - left}px`;
    frame.style.top = `${tableRect.top - stageRect.top}px`;
    frame.style.height = `${tableRect.height}px`;
    frame.hidden = false;
  }

  /**
   * AIのタグを表の外の上に置く（2026-08-21）。
   *
   * ★ セル内 absolute をやめた理由 ★
   * はみ出しても「表の中」に見えるという指摘（日高さん）。ai-frame と同じ
   * 「表に1枚重ねる」方式にして、表の上端より上へ出す。
   *
   * 位置は列の実寸から取る。列の幅は中身（機種名の折り返し・価格の桁）で決まり、
   * 比較APIの値が後から入った時点で変わるため、ResizeObserver が再度呼ぶ。
   */
  positionAiTags() {
    const layer = this.refs.aiTags;
    const stage = this.refs.stage;
    if (!layer || !stage) return;

    layer.innerHTML = '';
    const picks = this.aiPicks || [];
    if (picks.length === 0) {
      layer.hidden = true;
      return;
    }

    const stageRect = stage.getBoundingClientRect();
    const tableRect = this.refs.table.getBoundingClientRect();
    let placed = 0;

    for (const pick of picks) {
      // 機種ヘッダ行のセルから列の位置を取る。列が落ちていれば置かない
      const wrap = this.refs.table.querySelector(
        `.aicon-compare-page__model.is-ai[data-handle="${CSS.escape(pick.handle)}"]`,
      );
      if (!wrap) continue;
      const cellRect = wrap.getBoundingClientRect();

      const tag = document.createElement('span');
      tag.className = 'aicon-compare-page__ai';
      tag.textContent = pick.badge || 'AIのおすすめ';
      // 列の中央。表の上端より上へ出す（タグの高さ 22px ぶん）
      tag.style.left = `${cellRect.left - stageRect.left + cellRect.width / 2}px`;
      tag.style.top = `${tableRect.top - stageRect.top - 22}px`;
      // どの列のタグかを持たせる。スクロールのたびに列の位置を測り直す
      tag.dataset.handle = pick.handle;
      layer.appendChild(tag);
      placed += 1;
    }

    layer.hidden = placed === 0;
    this.syncAiTagVisibility();
  }

  /**
   * 固定列の裏に回った列のタグを隠す。
   *
   * ★ なぜ z-index では直らないか（日高さん指摘 2026-08-23）★
   *   タグは**表の外**（stage の padding-top 28px の中）に置いている。
   *   固定される項目名列（`th[scope='row']`）は**表の中**なので、
   *   両者は縦に重なっていない。∴ 重ね順をいくら変えても、
   *   「タグが機種・本体価格の欄の上に乗って見える」状態は消えない。
   *   実際に z-index を上げてみて変わらないことを確認した。
   *
   * ★ 起きていること ★
   *   横にスクロールすると列は左へ流れるが、項目名列は左端に留まる。
   *   流れた列のタグは**項目名列の真上**に来る。タグは列の目印なので、
   *   その列が見えていないのに残っていると、どの列の話か分からない。
   *
   * ★ 直し方 ★
   *   列の右端が項目名列の右端より左に来たら（＝裏に回ったら）そのタグを隠す。
   *   判定はスクロールのたびに行う。CSS では列の位置を知れないので JS でやる。
   */
  syncAiTagVisibility() {
    const layer = this.refs.aiTags;
    const scroller = this.refs.scroll;
    if (!layer || !scroller) return;
    const label = this.refs.table?.querySelector('th[scope="row"]');
    // 固定列が無い（レイアウト崩れ）ときは何も隠さない。出しっぱなしの方が安全
    const labelRight = label ? label.getBoundingClientRect().right : 0;

    for (const tag of layer.querySelectorAll('.aicon-compare-page__ai')) {
      const handle = tag.dataset.handle;
      const cell = handle
        ? this.refs.table.querySelector(
            `.aicon-compare-page__model[data-handle="${CSS.escape(handle)}"]`,
          )
        : null;
      if (!cell) continue;
      const rect = cell.getBoundingClientRect();
      // 列がまだ半分以上見えているなら出す。裏に回りかけたら消す
      const visible = rect.right - labelRight > rect.width * 0.5;
      tag.style.visibility = visible ? '' : 'hidden';
    }
  }

  /**
   * 表のサイズ変化に枠を追従させる。列の幅は中身（機種名の折り返し・価格の桁）で決まるため、
   * 画面幅を変えなくても後から比較APIの値が入った時点で変わる。ResizeObserver が
   * 無い環境では初期配置のままにする（枠がずれるだけで表は成立する）。
   */
  observeTableResize() {
    // 横スクロールでタグの出し入れを追従させる（listener は1回だけ張る）
    if (!this.scrollBound && this.refs.scroll) {
      this.scrollBound = true;
      this.refs.scroll.addEventListener('scroll', () => this.syncAiTagVisibility(), {
        passive: true,
      });
    }
    if (this.tableObserver || typeof ResizeObserver === 'undefined') return;
    this.tableObserver = new ResizeObserver(() => {
      this.positionAiFrame();
      this.positionAiTags();
      // 列幅が変われば折り返し行数も変わる。溢れ判定を取り直す。
      this.syncAiWhyToggles();
    });
    this.tableObserver.observe(this.refs.table);
  }

  /** ページから外れたら監視も畳む（切り離された表を観測し続けないため）。 */
  disconnectedCallback() {
    this.tableObserver?.disconnect();
    this.tableObserver = null;
  }

  /**
   * 「＋ 機種を追加する」の出し分け（日高さん指示 2026-08-20）。
   *
   * ★ 数えるのは「お客様が自分で選んだ数」★
   * AI の1台は maxModels の**外側**に足しているので、列数で数えると
   * 「AI + 2機種 = 3列」でまだ1枠空いているのに隠れてしまう。
   * 逆に枠が埋まっているのにボタンを出すと、一覧へ飛んでも4つ目を選べず
   * **押しても何も起きない導線**になる。判断の基準は列数ではなく枠の空き。
   *
   * 日高さんの確認済み（2026-08-20）:
   *   AI + 3機種（4列）→ 非表示 ／ AI + 2機種（3列）→ 表示（あと1枠）
   *      3機種（3列）→ 非表示 ／    2機種（2列）→ 表示
   */
  /**
   * 「＋ 機種を追加する」を出すか決める。
   *
   * ★ 数えるのは「お客様が自分で選んだ列」だけ（日高さん指摘 2026-08-23）★
   *   AI の提案は maxModels の外側にあるので、合計の列数で判断してはいけない。
   *
   * ★ 以前のバグ ★
   *   `column.handle !== this.aiHandle` で数えていたが、**`this.aiHandle` は
   *   2026-08-21 の複数提案対応で削除した変数**（resolveHandles の
   *   「★ aiHandle は持たない ★」参照）。undefined と比較するので条件は常に真、
   *   つまり **AI の列も「お客様の選択」として数えていた**。
   *   AI が3件以上提案すると `userPicked >= 3` になり、
   *   **「機種を追加する」が消えて1件も足せなくなっていた**（実機で確認）。
   *   複数化のときに参照を寄せ切れず、ここだけ残っていた。
   */
  syncAddVisibility(columns) {
    if (!this.refs.add) return;
    const aiHandles = new Set((this.aiPicks || []).map((p) => p.handle));
    const userPicked = columns.filter((column) => !aiHandles.has(column.handle)).length;
    this.refs.add.hidden = userPicked >= this.maxModels;
  }

  /**
   * 取得できなかった列があることをお客様にも伝える。
   *
   * ★ 黙って消さない ★
   *   列が落ちる理由は /products/<handle> の 404（オンラインストアに未公開・
   *   取扱終了）で、比較表側では直せない。ただ消えると「提案は3件あったのに
   *   1列しかない」が仕様に見えるため、件数だけでも画面に出す。
   */
  showDroppedNotice(count) {
    if (!count || count < 1) return;
    const host = this.refs.heading?.parentElement || this.refs.heading;
    if (!host) return;
    let box = this.querySelector('[data-compare-dropped]');
    if (!box) {
      box = document.createElement('p');
      box.className = 'aicon-compare__dropped';
      box.setAttribute('data-compare-dropped', '');
      box.setAttribute('role', 'status');
      host.appendChild(box);
    }
    box.textContent = `${count}機種は現在お取り扱いを確認できないため、比較から外しました。`;
  }

  showEmpty() {
    this.refs.scroll.hidden = true;
    if (this.refs.airo) this.refs.airo.hidden = true;
    this.refs.banner.hidden = true;
    if (this.refs.aiFrame) this.refs.aiFrame.hidden = true;
    this.refs.empty.hidden = false;
    this.refs.heading.textContent = '機種を比較する';
    this.refs.lead.hidden = true;
    this.refs.add.hidden = true;
  }

  async fetchColumn(handle) {
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}?sections=${COLUMN_SECTION}`);
      if (!res.ok) return null;
      const sections = await res.json();
      const html = sections[COLUMN_SECTION];
      if (!html) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const payload = doc.querySelector('[data-aicon-compare-product]');
      if (!payload) return null;
      return JSON.parse(payload.textContent);
    } catch {
      return null;
    }
  }

  /**
   * 行のセルに値を書き直す（試算3行を比較API応答で後から埋めるため）。
   * builder は td を空にした状態で呼ばれる。
   */
  setRow(row, values, builder) {
    row.querySelectorAll('td').forEach((td, index) => {
      td.textContent = '';
      builder(td, values[index], index);
    });
  }

  /**
   * その行で最良の列に「1位」を付ける。
   *
   * ★ 色だけで示さない ★ 太字＋バッジを併用する（色覚の配慮）。
   * ★ 引き分けは隠さない ★ 同値が複数あればすべて1位にする。
   * ★ 全列同値・比較対象が1つだけのときは付けない ★ 差が無いのに順位を出すと誤読させる。
   */
  markBest(row, values, direction) {
    const cells = [...row.querySelectorAll('td')];
    for (const td of cells) {
      td.classList.remove('is-best');
      td.querySelector('.aicon-compare-page__best')?.remove();
    }

    const numbers = values.filter((value) => typeof value === 'number' && Number.isFinite(value));
    if (new Set(numbers).size < 2) return;

    const best = direction === HIGHER_IS_BETTER ? Math.max(...numbers) : Math.min(...numbers);
    values.forEach((value, index) => {
      if (value !== best || !cells[index]) return;
      const td = cells[index];
      td.classList.add('is-best');
      const badge = document.createElement('span');
      badge.className = 'aicon-compare-page__best';
      badge.textContent = '1位';
      td.appendChild(badge);
    });
  }

  buildTable(columns) {
    const table = this.refs.table;
    table.textContent = '';
    // 表ごと作り直すので、前回の <p>/<button> への参照は捨てる
    // （残すと切り離された要素を計測し続けることになる）
    this.whyToggles = [];

    const always = document.createElement('tbody');

    // AI が提案した列の集合。行ごとに .is-ai を付けるのに使う（複数対応）
    const aiHandleSet = new Set((this.aiPicks || []).map((p) => p.handle));

    const addRow = (tbody, className, headerText, buildCell) => {
      const tr = document.createElement('tr');
      tr.className = className;
      const th = document.createElement('th');
      th.scope = 'row';
      th.textContent = headerText;
      tr.appendChild(th);
      columns.forEach((column, index) => {
        const td = document.createElement('td');
        // AI が選んだ列は全行に印を付ける。外枠（イメージカラーの囲み）は
        // sections/aicon-compare-table.liquid 側の CSS が .is-ai を見て引く。
        // ★ 提案が複数あるので全件に付ける（2026-08-21）★
        if (aiHandleSet.has(column.handle)) td.classList.add('is-ai');
        buildCell(td, column, index);
        tr.appendChild(td);
      });
      tbody.appendChild(tr);
      return tr;
    };

    // 機種ヘッダ行
    addRow(always, 'aicon-compare-page__row aicon-compare-page__row--model', '機種', (td, column) => {
      const wrap = document.createElement('div');
      wrap.className = 'aicon-compare-page__model';
      // タグの層が列の位置を測るのに使う（positionAiTags）
      wrap.dataset.handle = column.handle;
      // ★ AI のタグは列の上に浮かせる（日高さん指摘 2026-08-21）★
      //   以前は表の中に入っていて、機種名や画像を押し下げていた。
      //   .aicon-compare-page__model 側を position: relative にし、タグを
      //   その上へ absolute で載せて列の中央に置く（CSS 側で -translate）。
      //
      //   バッジの文言は診断SPA のスロット名（「一番おすすめ」「価格重視」等）を使う。
      //   渡ってこない場合だけ「AIのおすすめ」に落とす。
      const pick = (this.aiPicks || []).find((p) => p.handle === column.handle);
      if (pick) {
        // ★ タグはセルに入れない（日高さん指摘 2026-08-21）★
        //   セル内に absolute で載せると、はみ出しても「表の中」に見える。
        //   表の外の上に1枚重ねる層（positionAiTags）へ移した。
        //   ここでは枠と位置測定のための印だけ付ける。
        wrap.classList.add('is-ai');

        // 選定理由（LLM 生成）。
        //
        // ★ 折りたたみ＋トグルにした（日高さん指摘 2026-08-25）★
        //   以前は line-clamp: 4 で切りっぱなしだった。文は LLM 生成で長さが
        //   毎回変わるため、**文字を小さくしても収まる保証が無い**（短くすれば
        //   余り、長ければまた切れる）。かといって clamp を外すと、表の1行目が
        //   一番長い理由文の高さに揃って伸び、肝心の 本体価格／電気代／
        //   10年総コスト が画面の外へ落ちる（AI列は最大5列ある）。
        //   ∴ 畳んだ高さを3行で固定し、読みたい人だけ開く形にした。
        //
        //   ボタンは「実際に溢れている列だけ」出す（syncAiWhyToggles で計測）。
        //   3行に収まっている列にまで「続きを読む」が並ぶと、押しても何も
        //   起きないボタンになる。
        if (pick.reason) {
          const why = document.createElement('p');
          why.className = 'aicon-compare-page__aiWhy';
          why.id = `aicon-compare-why-${column.handle}`;
          why.textContent = pick.reason;
          wrap.appendChild(why);

          const more = document.createElement('button');
          more.type = 'button';
          more.className = 'aicon-compare-page__aiMore';
          // 三角と文字を別の要素にする。textContent で '▲ 畳む' と入れると
          // 読み上げが「くろさんかく たたむ」になるため。
          const moreCaret = document.createElement('span');
          moreCaret.className = 'aicon-compare-page__aiMore-caret';
          moreCaret.setAttribute('aria-hidden', 'true');
          const moreText = document.createElement('span');
          more.append(moreCaret, moreText);
          const setMoreLabel = (open) => {
            moreCaret.textContent = open ? WHY_LESS_CARET : '';
            moreText.textContent = open ? WHY_LESS_LABEL : WHY_MORE_LABEL;
          };
          setMoreLabel(false);
          more.setAttribute('aria-expanded', 'false');
          more.setAttribute('aria-controls', why.id);
          // 計測前は出さない（溢れていない列で一瞬ちらつくのを防ぐ）
          more.hidden = true;
          more.addEventListener('click', () => {
            const open = why.classList.toggle('is-open');
            setMoreLabel(open);
            more.setAttribute('aria-expanded', open ? 'true' : 'false');
            // 開閉で表の高さが変わる。AI列の外枠とタグは表の実寸を測って
            // 重ねているので、位置を取り直す（ResizeObserver も拾うが、
            // 押した直後に確実に合わせる）。
            this.positionAiFrame();
            this.positionAiTags();
          });
          wrap.appendChild(more);
          this.whyToggles.push({ why, more });
        }
      }
      // メーカーのロゴ。一覧のカードと同じ見え方に揃える（日高さん指摘 2026-08-19）。
      // 取扱外のメーカーは vendor_logo が null なので、その場合は出さない
      if (column.vendor_logo) {
        const logo = document.createElement('img');
        logo.className = 'aicon-compare-page__model-logo';
        logo.src = column.vendor_logo;
        logo.alt = column.vendor || '';
        logo.loading = 'lazy';
        logo.width = 72;
        logo.height = 18;
        wrap.appendChild(logo);
      }
      if (column.image) {
        const img = document.createElement('img');
        img.className = 'aicon-compare-page__model-image';
        img.src = column.image;
        img.alt = column.title || '';
        img.loading = 'lazy';
        wrap.appendChild(img);
      }
      if (column.room_size) {
        const size = document.createElement('span');
        size.className = 'aicon-compare-page__model-size';
        size.textContent = column.room_size;
        wrap.appendChild(size);
      }
      const title = document.createElement('span');
      title.className = 'aicon-compare-page__model-title';
      title.textContent = column.title || '';
      wrap.appendChild(title);
      if (column.model_no) {
        const code = document.createElement('span');
        code.className = 'aicon-compare-page__model-code';
        code.textContent = column.model_no;
        wrap.appendChild(code);
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'aicon-compare-page__remove';
      remove.textContent = '比較から外す';
      remove.addEventListener('click', () => this.removeColumn(column.handle));
      wrap.appendChild(remove);
      td.appendChild(wrap);
    });

    // 本体価格の行（T-24 / Q-10 = A）
    //
    // ★ 見出しごと「本体価格」に変えている ★
    // チケットは「本体 ◯◯円 ＋ 標準工事 ◯◯円ではなくて本体価格だけ記載する」。
    // 中身だけ本体価格にすると、見出しの「総額（工事費込み）」と矛盾するため、
    // 見出しと中身を同時に変えた。
    //
    // 比較ページは「どの機種にするか」を決める場所で、工事費は別商品として
    // 購入手続きで加算される（人気トップ4のカードから工事費表記を消した T-08 と同じ理由）。
    //
    // ★「工事費は全機種同額だから比べなくてよい」と書いていたのは誤り（2026-09-17 訂正）★
    //   標準工事セットは **能力で4段**（t1 2.2〜2.8 / t2 3.6〜4.0 / t3 5.6 / t4 6.3kW〜）ある。
    //   同額なのは**同じ能力クラス内だけ**で、クラスをまたぐと段が変わって金額も変わる。
    //   比較ページはお客様が一覧から任意の機種を並べる場所なので、クラスをまたぐ比較が普通に起きる。
    //
    //   ★ ここに金額を書かない（55-1 / 60-1 と同じ轍）★
    //     4段の実額は `pricing_configs`（category='install_set_fee'）から実行時に引かれ、
    //     管理画面から変えられる（backend の pricing-cache.service.ts）。
    //     `pricing-defaults.ts` にあるのは**seed 時の既定値**でしかない。
    //     コメントに数字を焼くと、値を変えたときにここだけ古いまま残る。
    //     現在値を知りたいときは工事費ページ（/pages/installation-fee）か
    //     backend の /api/compare/... が返す installationFee を見る。
    //
    //   ∴ この行を本体価格のみにしてよい理由は「同額だから」ではなく
    //   **見出しが「本体価格」だから**（工事費込みの額を本体価格として見せない）。
    //   段差は下の「10年総コスト」行が機種ごとの工事費を含めて吸収している
    //   （model-comparison.service.ts:266 totalPrice = unitPrice + installationFee）。
    // 総額は商品ページと「カートに入れる」のモーダルで機種ごとに正確に出る。
    //
    // ★ 値は solo_price（本体価格）だけを使う ★
    //   工事費は別商品（aicon-standard-install）で、見出しが「本体価格」なので工事込み総額を
    //   出すと工事費込みの額を本体価格として見せることになり、機種間の最安判定
    //   （markBest）も本体価格と総額を混ぜて比較してしまう。
    //   solo_price は同期漏れ・オプション名の揺れ・handle 不一致で null になりうる
    //   （checkout.ts が同じ理由で「本体のみバリアントが見つからない」を明示的に
    //   エラーにしている）。取れないときは金額を出さず「−」にする。
    const priceRow = addRow(
      always,
      'aicon-compare-page__row aicon-compare-page__row--price',
      '本体価格',
      (td, column) => {
        const body = column.solo_price;
        if (body == null) {
          td.textContent = '−';
          return;
        }
        const main = document.createElement('span');
        main.className = 'aicon-compare-page__price-main';
        main.textContent = new Intl.NumberFormat('ja-JP').format(body);
        const unit = document.createElement('span');
        unit.className = 'aicon-compare-page__price-unit';
        unit.textContent = '円';
        td.append(main, unit);
      }
    );
    this.markBest(
      priceRow,
      columns.map((column) => column.solo_price ?? null),
      LOWER_IS_BETTER,
    );

    // 試算3行（比較APIで後から埋める。それまでスケルトン）
    const skeletonCell = (td) => {
      const bar = document.createElement('span');
      bar.className = 'aicon-compare-page__skeleton';
      td.appendChild(bar);
    };
    this.estimateRows = {
      electricity: addRow(always, 'aicon-compare-page__row aicon-compare-page__row--estimate', '電気代 / 年', skeletonCell),
      tenYear: addRow(always, 'aicon-compare-page__row aicon-compare-page__row--estimate', '10年総コスト', skeletonCell),
      // ★ 「機能性スコア」の行はグレードに置き換えた（日高さん指示 2026-10-05）★
      //   商品画像の下の帯（スタンダード / ミドル / ハイグレード / プレミアム）と同じ言葉で比べる。
      //   値は比較APIの grade（backend utils/productGrade.ts）。
      grade: addRow(always, 'aicon-compare-page__row aicon-compare-page__row--estimate', 'グレード', skeletonCell),
    };

    // ★ 注記は表の中に入れない（8/19 日高さん指示）★
    //   以前は colspan で全列を横断する行として表の中に置いていた。長い文が
    //   横スクロールする表の中で折り返して読みづらく、列の高さも押し上げていた。
    //   表の下の別表（[data-ref="notes"]）に移し、差し込みは showNotes() が行う。
    this.setNote('electricity', this.dataset.loadingNote);
    this.setNote('tenYear', '');

    // CTA 行（折りたたみの外。常に押せる）
    const cta = document.createElement('tbody');
    addRow(cta, 'aicon-compare-page__row', '', (td, column) => {
      const link = document.createElement('a');
      link.className = 'aicon-compare-page__cta';
      link.href = this.productHrefWithDiagnosis(column.url);
      link.textContent = 'この機種を見る ›';
      td.appendChild(link);
    });

    table.append(always, cta);
    this.syncAiWhyToggles();
  }

  /**
   * 「続きを読む」を、実際に3行から溢れている列にだけ出す。
   *
   * ★ 計測は描画の後 ★
   *   scrollHeight / clientHeight は表を DOM に入れてからでないと 0 になる。
   *   さらに Web フォントの差し替えで行数が変わるため、fonts.ready でもう一度測る。
   *   （測り直さないと、フォント適用後に4行目が生えても畳んだままボタンが出ない）
   *
   * ★ 開いている列は触らない ★
   *   再計測のたびに畳むと、読んでいる途中で閉じられる。
   */
  syncAiWhyToggles() {
    const measure = () => {
      (this.whyToggles || []).forEach(({ why, more }) => {
        if (!why.isConnected) return;
        if (why.classList.contains('is-open')) {
          more.hidden = false;
          return;
        }
        // 1px の余裕は、行の端数で常に溢れ判定になるのを避けるため
        more.hidden = why.scrollHeight <= why.clientHeight + 1;
      });
    };
    measure();
    if (document.fonts?.ready) document.fonts.ready.then(measure).catch(() => {});
  }

  /**
   * 比較APIから受け取った AI提案の文言を反映する（2026-08-21）。
   *
   * ★ 型番で突き合わせる ★
   * URL に載っているのは handle、backend が持っているのは型番。
   * handle は型番から機械的に作られる（小文字化して英数以外を `-`）ため、
   * ここで同じ規則を掛けて対応付ける。backend/src/services/shopify/product-mapper.ts
   * の toHandle と規則を揃えること。
   *
   * ★ 反映できたときだけ描き直す ★
   * 空振りで render() を呼ぶと、試算の取得と描画が往復して無駄にちらつく。
   */
  applyRecommendation(rec) {
    /*
     * ★ picks の handle は backend が入れたものを使う（2026-09-17）★
     *   以前はここで型番から handle を作っていたが、型番は admin から訂正でき、
     *   訂正すると**URL の handle と食い違ってバッジも理由文も出なくなる**
     *   （2026-09-15 に三菱30機種で実発生）。
     *   backend は products.shopifyHandle を snapshot に載せて返す。
     *   古い snapshot には無いので、その場合だけ従来どおり型番から作る。
     */
    const toHandle = (modelNumber) =>
      String(modelNumber || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');

    const byHandle = new Map(
      rec.picks
        .map((p) => [String(p.shopifyHandle || '').trim() || toHandle(p.modelNumber), p])
        .filter(([handle]) => handle !== ''),
    );

    let changed = false;
    this.aiPicks = (this.aiPicks || []).map((pick) => {
      const found = byHandle.get(pick.handle);
      if (!found) return pick;
      const next = {
        handle: pick.handle,
        badge: typeof found.badge === 'string' ? found.badge : '',
        catchCopy: typeof found.catchCopy === 'string' ? found.catchCopy : '',
        reason: typeof found.reason === 'string' ? found.reason : '',
      };
      if (next.badge !== pick.badge || next.reason !== pick.reason) changed = true;
      return next;
    });

    const summary = typeof rec.summary === 'string' ? rec.summary : '';
    if (summary !== this.aiSummary) {
      this.aiSummary = summary;
      changed = true;
    }

    if (!changed) return;

    // 吹き出しはここで直接更新する（表の外なので再描画に巻き込まれない）
    if (this.refs.airo && this.refs.airoText) {
      this.refs.airoText.textContent = this.aiSummary;
      this.refs.airo.hidden = this.aiSummary === '';
    }
    // バッジ・理由は機種ヘッダ行の中なので、表を描き直して反映する
    this.render();
  }

  removeColumn(handle) {
    this.handles = this.handles.filter((h) => h !== handle);
    writeStorage(readStorage().filter((item) => item.handle !== handle));
    // AI列も外せる（8/19 日高さん判断）。syncUrl() が ?ai= から落とすので以降は出ない。
    this.aiPicks = (this.aiPicks || []).filter((p) => p.handle !== handle);
    this.refs.live.textContent = '機種を比較から外しました';
    this.render();
  }

  async fillEstimates(columns, generation) {
    // 古い応答を新しい表に書かないための世代チェック。await をまたぐたびに確認する
    const stale = () => generation !== this.renderGeneration;
    const dash = (td) => {
      td.textContent = '−';
    };
    const fallback = () => {
      if (stale()) return;
      const empty = columns.map(() => null);
      this.setRow(this.estimateRows.electricity, empty, dash);
      this.setRow(this.estimateRows.tenYear, empty, dash);
      this.setRow(this.estimateRows.grade, empty, dash);
      this.markBest(this.estimateRows.electricity, empty, LOWER_IS_BETTER);
      this.markBest(this.estimateRows.tenYear, empty, LOWER_IS_BETTER);
      this.showNotes(this.dataset.note);
      this.refs.banner.hidden = false;
    };

    const models = columns.map(modelKey).filter(Boolean);
    if (!this.apiBase || models.length === 0) {
      fallback();
      return;
    }

    try {
      // ★ AI提案の文言は API から取る（2026-08-21）★
      //   診断SPA とストアは別オリジンで sessionStorage を共有できない。
      //   ブラウザに保存すると別の診断の提案が復活する（ゾンビ）ため、
      //   quoteId を渡して毎回 backend から引く。
      const qs = new URLSearchParams({ models: models.join(',') });
      if (this.aiQuoteId) qs.set('quoteId', this.aiQuoteId);
      const res = await fetch(`${this.apiBase}/api/compare/models?${qs.toString()}`);
      if (!res.ok) throw new Error(`compare api ${res.status}`);
      const body = await res.json();
      if (stale()) return;
      const items = body?.data?.items || [];

      // 取れたらバッジ・理由・アイロの一言を反映して描き直す。
      // 取れなくても表は成立する（AI 列の装飾が出ないだけ）
      const rec = body?.data?.recommendation;
      if (rec && Array.isArray(rec.picks) && rec.picks.length > 0) {
        this.applyRecommendation(rec);
      }
      // 比較APIは入力（queried）をトリム済みでそのまま返すので modelKey で列に対応付ける
      const byQueried = new Map(items.map((item) => [item.queried, item]));

      let hasMissing = false;
      // 表示と 1位判定の両方に使うので、整形前の数値のまま取り出す
      const pick = (column, selector) => {
        const item = byQueried.get(modelKey(column));
        const value = item && item.found ? selector(item) : null;
        if (value == null) {
          hasMissing = true;
          return null;
        }
        return value;
      };

      const electricity = columns.map((column) => pick(column, (item) => item.annualElectricityCost ?? null));
      this.setRow(this.estimateRows.electricity, electricity, (td, value) => {
        td.textContent = value == null ? '−' : yen(value);
      });
      this.markBest(this.estimateRows.electricity, electricity, LOWER_IS_BETTER);

      const tenYear = columns.map((column) => pick(column, (item) => item.tenYearTotalCost ?? null));
      this.setRow(this.estimateRows.tenYear, tenYear, (td, value) => {
        td.textContent = value == null ? '−' : yen(value);
      });
      this.markBest(this.estimateRows.tenYear, tenYear, LOWER_IS_BETTER);

      // ★ グレードには「1位」を付けない ★
      //   金額は安いほど良いと言えるが、グレードは上が良いとは限らない（用途と予算で選ぶもの）。
      //   上位グレードに「1位」を付けると、いちばん高い機種を勧めているように見える。
      // ★ グレードが無くても警告バナーは出さない（pick を通さない）★
      //   grade は表（productGrade.ts）に載っているシリーズにしか付かない。pick を通すと
      //   表に無いシリーズを1列入れただけで「一部の試算を取得できませんでした」が出る。
      //   機種そのものが取れていない（found:false）ときは、上の電気代の pick が既にバナーを出す。
      const grade = columns.map((column) => {
        const item = byQueried.get(modelKey(column));
        return item && item.found && item.grade ? item : null;
      });
      this.setRow(this.estimateRows.grade, grade, (td, item) => {
        if (item == null) {
          td.textContent = '−';
          return;
        }
        const label = document.createElement('span');
        label.className = 'aicon-compare-page__grade';
        label.textContent = item.grade;
        td.append(label);
        if (item.coldRegion) {
          const cold = document.createElement('span');
          cold.className = 'aicon-compare-page__grade-cold';
          cold.textContent = '寒冷地';
          td.append(cold);
        }
      });

      this.showNotes(body?.data?.electricityNote || this.dataset.note);
      this.refs.banner.hidden = !hasMissing;
      this.refs.live.textContent = '電気代と10年総コストの試算を表示しました';
    } catch {
      fallback();
    }
  }

  /**
   * 注記の表示。電気代の注記は比較API（electricityNote）が持っているが、
   * それは電気代のことしか書いていない。10年総コストにも同じ粒度の補足を出す（T-24）。
   */
  showNotes(electricityNote) {
    this.setNote('electricity', electricityNote);
    this.setNote('tenYear', this.dataset.tenYearNote || '');
  }

  /**
   * 表の下の「前提」表に1行ぶんの文言を入れる。
   *
   * 行は器（liquid）側に置いてあり、ここは中身を差し替えるだけ。
   * 文言が空の行は行ごと隠す（見出しだけ残ると何の話か分からなくなる）。
   */
  setNote(key, text) {
    const row = this.refs.notes?.querySelector(`[data-note-row="${key}"]`);
    if (!row) return;
    const body = row.querySelector('[data-note-body]');
    if (body) body.textContent = text || '';
    row.hidden = !text;
    // 2行とも空なら表ごと隠す
    const anyVisible = [...this.refs.notes.querySelectorAll('[data-note-row]')].some((r) => !r.hidden);
    this.refs.notes.hidden = !anyVisible;
  }
}

if (!customElements.get('aicon-compare-table')) {
  customElements.define('aicon-compare-table', AiconCompareTable);
}
